import { resolveLocale } from '@powerful/config/i18n';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../icons/icon';

/**
 * Estados da descoberta (canvas HomeLoading/HomeEmpty/HomeError/HomeOffline/ResultsEmpty, C7).
 *
 * Cada estado diz o que aconteceu E o que fazer a seguir — um vazio sem ação é beco sem saída.
 * `role="status"`: nenhum destes é erro do utilizador, e um `alert` interromperia a leitura.
 */
export function FeedSkeleton({ count = 6 }: { count?: number }) {
  const { t } = useTranslation();
  return (
    <div className="pc-sec" data-testid="feed-skeleton">
      <p className="pc-vh" role="status">{t('discovery.results.loading')}</p>
      <ul className="pc-feed" aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <li key={i}>
            <div className="card pc-bcard">
              <div className="pc-sk" style={{ aspectRatio: '16 / 9', borderRadius: 0 }} />
              <div className="pc-bcard-body">
                <div className="pc-sk" style={{ height: 18, width: '70%' }} />
                <div className="pc-sk" style={{ height: 12, width: '90%' }} />
                <div className="pc-sk" style={{ height: 12, width: '45%' }} />
                <div className="pc-sk" style={{ height: 22, width: '30%' }} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface StateMessageProps {
  icon: IconName;
  title: string;
  text: string;
  actions?: ReactNode;
  testId?: string;
}

export function StateMessage({ icon, title, text, actions, testId }: StateMessageProps) {
  return (
    <div className="empty" role="status" data-testid={testId}>
      <div className="orb" aria-hidden="true"><Icon name={icon} size={28} /></div>
      <h2 className="pc-h2 mb-2">{title}</h2>
      <p className="pc-body mx-auto mb-5 max-w-[40ch]">{text}</p>
      {actions ? <div className="pc-state-actions">{actions}</div> : null}
    </div>
  );
}

export interface NoticeProps {
  tone: 'info' | 'warn';
  icon: IconName;
  children: ReactNode;
  action?: ReactNode;
  testId?: string;
}

export function Notice({ tone, icon, children, action, testId }: NoticeProps) {
  return (
    <div className={`alert alert-${tone} min-w-0 flex-wrap items-center`} role="status" data-testid={testId}>
      <span className={tone === 'warn' ? 'text-warn' : 'text-info'}><Icon name={icon} size={18} /></span>
      <span className="min-w-0 flex-1 text-[length:var(--t-sm)]">{children}</span>
      {action}
    </div>
  );
}

/** «Guardado há 3 h» — idade visível do snapshot offline (C7). */
export function useSavedAgo(savedAt: number | undefined, now = Date.now()): string | null {
  const { i18n } = useTranslation();
  if (savedAt === undefined) return null;
  const rtf = new Intl.RelativeTimeFormat(resolveLocale(i18n.language), { numeric: 'auto' });
  const minutes = Math.round((savedAt - now) / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  return rtf.format(Math.round(minutes / 60), 'hour');
}
